import { useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Button, Modal, Notification, ScrollView, Text, styles, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import { RoomCard } from './Booking.RoomCard';
import { DATASOURCE } from '../../__shared__/Occupation/Occupation.constants';
import { L10N } from '../Booking.l10n';
import * as style from '../Booking.module.css';
import { getDetailsByRoom, getRoomDataSource } from '../helpers';

const ModalRoomSelection = ({
  dataSource = {},
  rooms: propRooms,
  title,
  visible,
  onBack = () => {},
  onClose = () => {},
  onNext = () => {},
  ...others
}) => {
  const { isMobile } = useDevice();

  const { translate } = useLocale();

  const { value: storeValue = {} } = useStore() || {};

  const [room, setRoom] = useState();

  useEffect(() => {
    if (!visible) return;

    setRoom();
  }, [visible]);

  const { details = [], modify: { occupation: { rooms: defaultRooms = [] } = {} } = {} } = dataSource;
  const configDataSource = Array.isArray(storeValue?.occupation?.dataSource)
    ? storeValue.occupation.dataSource
    : DATASOURCE;
  const rooms = propRooms || defaultRooms;
  const roomDataSource = useMemo(
    () => getRoomDataSource({ configDataSource, details, occupation: rooms, translate }),
    [configDataSource, details, rooms, translate],
  );
  const totalRooms = useMemo(
    () => getDetailsByRoom(details).length || roomDataSource.length,
    [details, roomDataSource.length],
  );
  const hasPartialRoomSelection = totalRooms > 1 && roomDataSource.length < totalRooms;

  const { testId } = others;

  return (
    <Modal
      {...others}
      fit
      title={title}
      visible={visible}
      onClose={onClose}
      className={styles(style.modalRoomSelection, others.className)}
      testId={`${testId}-modal-room-selector`}
    >
      <View>
        <View className={style.panel} testId={`${testId}-room-selector`}>
          <View className={style.body}>
            <Text bold paragraph>
              {translate(L10N.LABEL_SELECT_ROOM)}
            </Text>
            <ScrollView horizontal={false} snap={false} className={style.scrollView}>
              <View className={style.list}>
                {roomDataSource.map(({ image, index, occupation, plan, rate, room: roomValue, title }) => (
                  <RoomCard
                    key={`${roomValue?.id}-${roomValue?.index ?? index}`}
                    selected={room?.id === roomValue?.id && room?.index === roomValue?.index}
                    onPress={() => setRoom(roomValue)}
                    testId={`${testId}-room-${index}`}
                    {...{ image, occupation, plan, rate, title }}
                  />
                ))}
              </View>
            </ScrollView>
            {hasPartialRoomSelection && (
              <Notification info wide testId={`${testId}-room-notification`}>
                {translate(L10N.NOTIFICATION_EDIT_PARTIAL_ROOM_SELECTION)}
              </Notification>
            )}
          </View>
          <View row className={style.footerActions}>
            {isMobile && (
              <Button secondary onPress={onBack} testId={`${testId}-room-back`}>
                {translate(L10N.ACTION_BACK)}
              </Button>
            )}
            <Button disabled={room === undefined} onPress={() => onNext(room)} testId={`${testId}-room-next`}>
              {translate(L10N.ACTION_NEXT)}
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
};

ModalRoomSelection.displayName = 'Mirai:Core:Booking:ModalRoomSelection';

ModalRoomSelection.propTypes = {
  dataSource: PropTypes.shape({
    details: PropTypes.arrayOf(PropTypes.shape({})),
    modify: PropTypes.shape({
      occupation: PropTypes.shape({
        rooms: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            maxOccupation: PropTypes.shape({
              adults: PropTypes.number,
              children: PropTypes.number,
            }),
            minOccupation: PropTypes.shape({
              adults: PropTypes.number,
              children: PropTypes.number,
            }),
          }),
        ),
      }),
    }),
  }),
  rooms: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  title: PropTypes.string,
  visible: PropTypes.bool,
  onBack: PropTypes.func,
  onClose: PropTypes.func,
  onNext: PropTypes.func,
};

export { ModalRoomSelection };
